import "#/polyfills/Uint8Array_base64"
import { basename, extname } from "node:path"

import { css } from "@emotion/css"
import { Button, Chip } from "@heroui/react"
import { Plural, Trans, useLingui } from "@lingui/react/macro"
import type { FileTypeResult } from "file-type"
import { Download } from "lucide-react"

import type { Content } from "../../har"
import * as mime from "../../mime"

export function BodyHeader({
  url,
  content,
  fileType,
}: {
  url: string
  content: Content
  fileType: FileTypeResult | undefined
}) {
  const { t } = useLingui()
  const text = content.text!
  const mimeType = content.mimeType
  const fileName = basename(url, extname(url))

  return (
    <div css="mb-2 ml-2 mr-3 mt-1 flex border-b border-solid border-b-gray-100 dark:border-b-gray-800">
      <div className="flex grow gap-2 pb-2">
        <KV
          name={t`MIME`}
          value={fileType ? `${mimeType} (guess: ${fileType.mime})` : mimeType}
        />
        <KV
          name={t`Size`}
          value={<Plural one="# byte" other="# bytes" value={content.size} />}
        />
        {content.encoding !== "base64" && <KV name="Encoding" value={content.encoding} />}
      </div>
      <div>
        <Button
          size="sm"
          startContent={<Download size="14" />}
          onPress={() => {
            const name = mimeType
              ? `${fileName}.${mime.extension(mimeType)}`
              : basename(url)
            const blob = new Blob(
              [content.encoding === "base64" ? Uint8Array.fromBase64(text) : text],
              { type: mimeType }
            )
            const href = URL.createObjectURL(blob)
            const a = document.createElement("a")
            a.href = href
            a.download = name
            a.click()
            URL.revokeObjectURL(href)
          }}
        >
          <Trans>Download</Trans>
        </Button>
      </div>
    </div>
  )
}

function KV({ name, value }: { name: string; value?: React.ReactNode }) {
  if (value === undefined) {
    return null
  }

  return (
    <div
      className={css`
        &:not(:last-child) {
          border-right: 0.5px solid light-dark(#eee, #333);
          padding-right: 7px;
        }
      `}
    >
      <Chip radius="sm" variant="flat">
        {name}
      </Chip>{" "}
      <span className="text-sm">{value}</span>
    </div>
  )
}
